// RiskDetailTab — the default tab showing risk fields with inline editing.
// Row 1: Description + Risk Grid. Row 2: Scoring cards (draft with Apply).
// Row 3: Classification (editable taxonomy + lifecycle transitions). Row 4: Summary.

import { useState, useEffect, useMemo, useCallback } from 'react'
import { useParams } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useApi } from '@/lib/api/client'
import { useTenant } from '@/components/shared/TenantShell'
import {
  risksApi,
  type Risk,
  type UpdateRiskRequest,
  type ImpactAssessmentInput,
  lifecycleStateLabels,
  lifecycleStateColors,
} from '@/lib/api/risks'
import {
  scoringApi,
  type ActiveLikelihood,
  type ImpactTableWithLevels,
  type ImpactAssessment,
} from '@/lib/api/scoring'
import { queryKeys } from '@/lib/api/queryKeys'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { FormSkeleton } from '@/components/shared/LoadingSkeleton'
import { Pencil, Check, X, Save, RotateCcw, ArrowRight, ChevronDown, ChevronRight, AlertTriangle } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  RiskGrid,
  RiskJourney,
  appetiteKey,
  type GridConfig,
  type AppetiteMap,
  type RiskJourneyData,
} from '@/components/features/risk-grid'

type ScoreType = 'residual' | 'inherent' | 'target'

const SCORE_TYPES: ScoreType[] = ['inherent', 'residual', 'target']

const SCORE_TYPE_CONFIG: Record<
  ScoreType,
  {
    title: string
    likelihoodField:
      | 'inherent_likelihood_level_id'
      | 'residual_likelihood_level_id'
      | 'target_likelihood_level_id'
    colorVar: string
  }
> = {
  inherent: {
    title: 'Inherent',
    likelihoodField: 'inherent_likelihood_level_id',
    colorVar: '--grid-node-inherent',
  },
  residual: {
    title: 'Current (Residual)',
    likelihoodField: 'residual_likelihood_level_id',
    colorVar: '--grid-node-residual',
  },
  target: {
    title: 'Target',
    likelihoodField: 'target_likelihood_level_id',
    colorVar: '--grid-node-target',
  },
}

// --- Valid lifecycle transitions ---
const VALID_TRANSITIONS: Record<string, { target: string; label: string }[]> = {
  draft: [{ target: 'identify', label: 'Move to Identify' }],
  identify: [{ target: 'assess', label: 'Move to Assess' }],
  assess: [{ target: 'manage', label: 'Move to Manage' }],
  manage: [{ target: 'closed', label: 'Close Risk' }],
  closed: [{ target: 'manage', label: 'Reopen Risk' }],
}

const TRANSITION_ENDPOINTS: Record<string, 'identify' | 'assess' | 'manage' | 'close' | 'reopen'> =
  {
    identify: 'identify',
    assess: 'assess',
    manage: 'manage',
    closed: 'close',
  }

// Reopen is special: closed→manage uses 'reopen' endpoint
function getTransitionEndpoint(
  currentState: string,
  targetState: string,
): 'identify' | 'assess' | 'manage' | 'close' | 'reopen' {
  if (currentState === 'closed' && targetState === 'manage') return 'reopen'
  return TRANSITION_ENDPOINTS[targetState] ?? 'identify'
}

// --- Draft state types ---

interface ScoringDraft {
  likelihoods: Record<ScoreType, string | null>
  impacts: Record<string, string | null>
}

function impactDraftKey(scoreType: ScoreType, tableId: string): string {
  return `${scoreType}:${tableId}`
}

function buildInitialDraft(risk: Risk, assessments: ImpactAssessment[]): ScoringDraft {
  const impacts: Record<string, string | null> = {}
  for (const a of assessments) {
    impacts[impactDraftKey(a.score_type as ScoreType, a.impact_table_id)] = a.impact_level_id
  }
  return {
    likelihoods: {
      inherent: risk.inherent_likelihood_level_id ?? null,
      residual: risk.residual_likelihood_level_id ?? null,
      target: risk.target_likelihood_level_id ?? null,
    },
    impacts,
  }
}

function draftsEqual(a: ScoringDraft, b: ScoringDraft): boolean {
  for (const st of SCORE_TYPES) {
    if (a.likelihoods[st] !== b.likelihoods[st]) return false
  }
  const allKeys = new Set([...Object.keys(a.impacts), ...Object.keys(b.impacts)])
  for (const k of allKeys) {
    if ((a.impacts[k] ?? null) !== (b.impacts[k] ?? null)) return false
  }
  return true
}

// --- Taxonomy node type ---
interface TaxonomyNode {
  id: string
  name: string
  path: string
  depth: number
}

export function RiskDetailTab() {
  const { slug } = useTenant()
  const { riskId } = useParams({ strict: false }) as { riskId: string }
  const api = useApi()
  const riskApi = risksApi(api)
  const scoring = scoringApi(api)
  const queryClient = useQueryClient()

  // --- Data queries ---

  const { data: risk, isLoading: riskLoading } = useQuery({
    queryKey: queryKeys.risks.detail(slug, riskId),
    queryFn: () => riskApi.get(riskId),
  })

  const { data: likelihood } = useQuery({
    queryKey: queryKeys.scoring.likelihoodLevels(slug),
    queryFn: () => scoring.getLikelihood(),
  })

  const { data: impactTables } = useQuery({
    queryKey: queryKeys.scoring.impactTables(slug),
    queryFn: () => scoring.getImpactTables(),
  })

  const { data: currentAssessments } = useQuery({
    queryKey: queryKeys.risks.impactAssessments(slug, riskId),
    queryFn: () => scoring.getRiskImpactAssessments(riskId),
  })

  const { data: appetiteData } = useQuery({
    queryKey: queryKeys.scoring.appetite(slug, risk?.taxonomy_node_id ?? ''),
    queryFn: () => scoring.resolveAppetite(risk!.taxonomy_node_id),
    enabled: !!risk,
  })

  const { data: taxonomyData } = useQuery({
    queryKey: queryKeys.scoring.taxonomyNodes(slug),
    queryFn: () => api.get<{ nodes: TaxonomyNode[] }>('/scoring/taxonomy-nodes'),
  })
  const taxonomyNodes = useMemo(() => taxonomyData?.nodes ?? [], [taxonomyData])

  // --- Scoring draft state ---

  const serverDraft = useMemo(
    () => (risk && currentAssessments ? buildInitialDraft(risk, currentAssessments) : null),
    [risk, currentAssessments],
  )

  const [draft, setDraft] = useState<ScoringDraft | null>(null)
  const [showNotesDialog, setShowNotesDialog] = useState(false)
  const [changeNotes, setChangeNotes] = useState('')
  const [pendingAction, setPendingAction] = useState<
    'scoring' | 'description' | 'transition' | 'taxonomy'
  >('scoring')

  useEffect(() => {
    if (serverDraft) setDraft(serverDraft)
  }, [serverDraft])

  const isScoringDirty = useMemo(
    () => draft !== null && serverDraft !== null && !draftsEqual(draft, serverDraft),
    [draft, serverDraft],
  )

  const setLikelihood = useCallback((scoreType: ScoreType, levelId: string | null) => {
    setDraft((prev) =>
      prev ? { ...prev, likelihoods: { ...prev.likelihoods, [scoreType]: levelId } } : prev,
    )
  }, [])

  const setImpact = useCallback((scoreType: ScoreType, tableId: string, levelId: string | null) => {
    setDraft((prev) =>
      prev
        ? { ...prev, impacts: { ...prev.impacts, [impactDraftKey(scoreType, tableId)]: levelId } }
        : prev,
    )
  }, [])

  const handleDiscardScoring = useCallback(() => {
    if (serverDraft) setDraft(serverDraft)
  }, [serverDraft])

  // --- Apply scoring mutation ---

  const applyMutation = useMutation({
    mutationFn: (req: UpdateRiskRequest) => riskApi.update(riskId, req),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.risks.detail(slug, riskId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.risks.impactAssessments(slug, riskId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.risks.history(slug, riskId) })
      // Invalidate register risk list so appetite/score flags refresh on navigation back
      queryClient.invalidateQueries({ queryKey: queryKeys.registers.all(slug) })
      setShowNotesDialog(false)
      setChangeNotes('')
    },
  })

  const handleApplyScoring = useCallback(() => {
    setPendingAction('scoring')
    setShowNotesDialog(true)
  }, [])

  const handleConfirmScoring = useCallback(() => {
    if (!draft || !serverDraft) return
    const req: UpdateRiskRequest = {}
    for (const st of SCORE_TYPES) {
      const field = SCORE_TYPE_CONFIG[st].likelihoodField
      if (draft.likelihoods[st] !== serverDraft.likelihoods[st]) {
        req[field] = draft.likelihoods[st] ?? undefined
      }
    }
    const assessments: ImpactAssessmentInput[] = []
    const allKeys = new Set([...Object.keys(draft.impacts), ...Object.keys(serverDraft.impacts)])
    for (const key of allKeys) {
      if ((draft.impacts[key] ?? null) !== (serverDraft.impacts[key] ?? null)) {
        const [scoreType, tableId] = key.split(':') as [ScoreType, string]
        const levelId = draft.impacts[key]
        if (levelId) {
          assessments.push({
            impact_table_id: tableId,
            impact_level_id: levelId,
            score_type: scoreType,
          })
        }
      }
    }
    if (assessments.length > 0) req.impact_assessments = assessments
    req.change_notes = changeNotes || 'Scoring updated'
    applyMutation.mutate(req)
  }, [draft, serverDraft, changeNotes, applyMutation])

  // --- Description draft state ---

  const [descEditing, setDescEditing] = useState(false)
  const [descName, setDescName] = useState('')
  const [descEvent, setDescEvent] = useState('')
  const [descDrivers, setDescDrivers] = useState('')
  const [descImpact, setDescImpact] = useState('')

  useEffect(() => {
    if (risk) {
      setDescName(risk.name)
      setDescEvent(risk.event_description)
      setDescDrivers(risk.drivers ?? '')
      setDescImpact(risk.impact_description ?? '')
    }
  }, [risk])

  const isDescDirty = useMemo(() => {
    if (!risk) return false
    return (
      descName !== risk.name ||
      descEvent !== risk.event_description ||
      (descDrivers || '') !== (risk.drivers ?? '') ||
      (descImpact || '') !== (risk.impact_description ?? '')
    )
  }, [risk, descName, descEvent, descDrivers, descImpact])

  const handleDescCancel = useCallback(() => {
    if (risk) {
      setDescName(risk.name)
      setDescEvent(risk.event_description)
      setDescDrivers(risk.drivers ?? '')
      setDescImpact(risk.impact_description ?? '')
    }
    setDescEditing(false)
  }, [risk])

  const handleDescApply = useCallback(() => {
    setPendingAction('description')
    setShowNotesDialog(true)
  }, [])

  const handleConfirmDesc = useCallback(() => {
    const req: UpdateRiskRequest = {
      change_notes: changeNotes || 'Description updated',
    }
    if (descName !== risk?.name) req.name = descName
    if (descEvent !== risk?.event_description) req.event_description = descEvent
    if ((descDrivers || '') !== (risk?.drivers ?? '')) req.drivers = descDrivers || undefined
    if ((descImpact || '') !== (risk?.impact_description ?? ''))
      req.impact_description = descImpact || undefined
    applyMutation.mutate(req, {
      onSuccess: () => {
        setDescEditing(false)
      },
    })
  }, [risk, descName, descEvent, descDrivers, descImpact, changeNotes, applyMutation])

  // --- Lifecycle transition ---

  const [pendingTransition, setPendingTransition] = useState<string | null>(null)

  const transitionMutation = useMutation({
    mutationFn: ({ endpoint, notes }: { endpoint: string; notes: string }) => {
      const fn = riskApi[endpoint as keyof ReturnType<typeof risksApi>] as (
        id: string,
        data?: { change_notes?: string },
      ) => Promise<Risk>
      return fn(riskId, { change_notes: notes })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.risks.detail(slug, riskId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.risks.history(slug, riskId) })
      // Invalidate register risk list so lifecycle state refreshes on navigation back
      queryClient.invalidateQueries({ queryKey: queryKeys.registers.all(slug) })
      setShowNotesDialog(false)
      setChangeNotes('')
      setPendingTransition(null)
    },
  })

  const handleTransition = useCallback((targetState: string) => {
    setPendingTransition(targetState)
    setPendingAction('transition')
    setShowNotesDialog(true)
  }, [])

  const handleConfirmTransition = useCallback(() => {
    if (!pendingTransition || !risk) return
    const endpoint = getTransitionEndpoint(risk.lifecycle_state, pendingTransition)
    transitionMutation.mutate({
      endpoint,
      notes:
        changeNotes ||
        `State changed: ${lifecycleStateLabels[risk.lifecycle_state]} → ${lifecycleStateLabels[pendingTransition]}`,
    })
  }, [pendingTransition, risk, changeNotes, transitionMutation])

  // --- Taxonomy change ---

  const [pendingTaxonomyNodeId, setPendingTaxonomyNodeId] = useState<string | null>(null)

  const handleTaxonomyChange = useCallback(
    (nodeId: string) => {
      if (nodeId === risk?.taxonomy_node_id) return
      setPendingTaxonomyNodeId(nodeId)
      setPendingAction('taxonomy')
      setShowNotesDialog(true)
    },
    [risk],
  )

  const handleConfirmTaxonomy = useCallback(() => {
    if (!pendingTaxonomyNodeId) return
    applyMutation.mutate(
      {
        taxonomy_node_id: pendingTaxonomyNodeId,
        change_notes: changeNotes || 'Taxonomy category changed',
      },
      {
        onSuccess: () => {
          setPendingTaxonomyNodeId(null)
        },
      },
    )
  }, [pendingTaxonomyNodeId, changeNotes, applyMutation])

  // --- Notes dialog confirm dispatch ---

  const handleConfirmApply = useCallback(() => {
    if (pendingAction === 'scoring') handleConfirmScoring()
    else if (pendingAction === 'description') handleConfirmDesc()
    else if (pendingAction === 'transition') handleConfirmTransition()
    else if (pendingAction === 'taxonomy') handleConfirmTaxonomy()
  }, [
    pendingAction,
    handleConfirmScoring,
    handleConfirmDesc,
    handleConfirmTransition,
    handleConfirmTaxonomy,
  ])

  const handleCancelDialog = useCallback(() => {
    setShowNotesDialog(false)
    setChangeNotes('')
    setPendingTransition(null)
    setPendingTaxonomyNodeId(null)
  }, [])

  // All taxonomy nodes except root (depth 0), sorted by path for tree ordering
  const selectableNodes = useMemo(
    () => taxonomyNodes.filter((n) => n.depth > 0).sort((a, b) => a.path.localeCompare(b.path)),
    [taxonomyNodes],
  )

  if (riskLoading || !risk) return <FormSkeleton />

  // --- Framework staleness detection ---
  // Taxonomy is stale if the risk's node ID is not in the current active taxonomy
  const taxonomyStale =
    taxonomyNodes.length > 0 && !taxonomyNodes.some((n) => n.id === risk.taxonomy_node_id)
  // Likelihood is stale if the risk references a level not in the current active set
  const likelihoodLevelIds = new Set(likelihood?.levels.map((l) => l.id) ?? [])
  const likelihoodStale =
    likelihoodLevelIds.size > 0 &&
    [
      risk.residual_likelihood_level_id,
      risk.inherent_likelihood_level_id,
      risk.target_likelihood_level_id,
    ].some((id) => id && !likelihoodLevelIds.has(id))
  const frameworkStale = taxonomyStale || likelihoodStale

  // --- Grid data (uses draft for live preview) ---

  const gridConfig = buildGridConfig(likelihood ?? null, impactTables ?? [])
  const appetiteMap = buildAppetiteMap(appetiteData?.permitted_cells ?? [])
  const journeyData = draft
    ? buildJourneyDataFromDraft(risk, draft, likelihood ?? null, impactTables ?? [])
    : buildJourneyData(risk, likelihood ?? null, impactTables ?? [], currentAssessments ?? [])

  const isMutating = applyMutation.isPending || transitionMutation.isPending

  return (
    <div className="space-y-6 p-6">
      {/* Framework staleness banner */}
      {frameworkStale && (
        <div className="flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-700 dark:bg-amber-950/30 dark:text-amber-200">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <p className="font-medium">This risk uses out-of-date framework values</p>
            <p className="mt-0.5 text-xs text-amber-700 dark:text-amber-300">
              {taxonomyStale && likelihoodStale
                ? 'The taxonomy category and likelihood scoring reference a previous framework version. Update them to align with the current framework.'
                : taxonomyStale
                  ? 'The taxonomy category references a previous framework version. Select a current category to align this risk.'
                  : 'The likelihood scoring references a previous framework version. Update the scoring to align with the current framework.'}
            </p>
          </div>
        </div>
      )}

      {/* Row 1: Description + Risk Grid */}
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[1fr_max-content]">
        <DescriptionCard
          editing={descEditing}
          setEditing={setDescEditing}
          name={descName}
          setName={setDescName}
          eventDescription={descEvent}
          setEventDescription={setDescEvent}
          drivers={descDrivers}
          setDrivers={setDescDrivers}
          impactDescription={descImpact}
          setImpactDescription={setDescImpact}
          isDirty={isDescDirty}
          onApply={handleDescApply}
          onCancel={handleDescCancel}
          isPending={isMutating}
          error={pendingAction === 'description' ? applyMutation.error : null}
        />

        {gridConfig && (
          <div className="w-[280px]">
            <Card className="h-full">
              <CardContent className="p-3">
                <div className="mb-2 text-xs text-muted-foreground">
                  Applicable Risk Appetite:{' '}
                  <span className="font-medium text-foreground">
                    {appetiteData?.taxonomy_node_name ?? '—'}
                  </span>
                </div>
                <RiskGrid config={gridConfig} appetite={appetiteMap}>
                  {journeyData && (
                    <RiskJourney
                      journey={journeyData}
                      cols={gridConfig.likelihoodLevels.length}
                      rows={gridConfig.impactLevels.length}
                    />
                  )}
                </RiskGrid>
                <div className="mt-2 flex items-center justify-center gap-4 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <span
                      className="inline-block h-3 w-3 rounded-full"
                      style={{ backgroundColor: 'hsl(var(--grid-node-inherent))' }}
                    />
                    Inherent
                  </span>
                  <span className="flex items-center gap-1">
                    <span
                      className="inline-block h-3 w-3 rounded-full"
                      style={{ backgroundColor: 'hsl(var(--grid-node-residual))' }}
                    />
                    Residual
                  </span>
                  <span className="flex items-center gap-1">
                    <span
                      className="inline-block h-3 w-3 rounded-full"
                      style={{ backgroundColor: 'hsl(var(--grid-node-target))' }}
                    />
                    Target
                  </span>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Appetite statement (full-width, between description/grid and scoring) */}
      {appetiteData?.statement && (
        <Card>
          <CardContent className="px-4 py-3">
            <div className="text-xs font-medium text-muted-foreground">
              Risk Appetite Statement
              <span className="ml-1 font-normal">({appetiteData.taxonomy_node_name})</span>
            </div>
            <p className="mt-1 text-sm">{appetiteData.statement}</p>
          </CardContent>
        </Card>
      )}

      {/* Row 2: Scoring section */}
      {(likelihood || (impactTables ?? []).length > 0) && draft ? (
        <>
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium">Risk Scoring</h3>
            <div className="flex items-center gap-2">
              {isScoringDirty && <span className="text-xs text-amber-600">Unsaved changes</span>}
              <Button
                variant="outline"
                size="sm"
                onClick={handleDiscardScoring}
                disabled={!isScoringDirty || isMutating}
              >
                <RotateCcw className="mr-1 h-3.5 w-3.5" /> Discard
              </Button>
              <Button
                size="sm"
                onClick={handleApplyScoring}
                disabled={!isScoringDirty || isMutating}
              >
                <Save className="mr-1 h-3.5 w-3.5" /> Apply Changes
              </Button>
            </div>
          </div>
          {pendingAction === 'scoring' && applyMutation.isError && (
            <div className="rounded bg-destructive/10 p-2 text-sm text-destructive">
              {(applyMutation.error as Error).message || 'Failed to save scoring changes'}
            </div>
          )}
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {SCORE_TYPES.map((scoreType) => (
              <ScoringCard
                key={scoreType}
                scoreType={scoreType}
                draft={draft}
                likelihood={likelihood ?? null}
                impactTables={impactTables ?? []}
                onLikelihoodChange={setLikelihood}
                onImpactChange={setImpact}
              />
            ))}
          </div>
        </>
      ) : (
        !likelihood &&
        (impactTables ?? []).length === 0 && (
          <p className="text-sm italic text-muted-foreground">
            No scoring framework configured. Configure likelihood and impact tables in Framework
            Settings.
          </p>
        )
      )}

      {/* Row 3: Classification & Ownership + Lifecycle */}
      <ClassificationCard
        risk={risk}
        taxonomyNodes={selectableNodes}
        onTaxonomyChange={handleTaxonomyChange}
        onTransition={handleTransition}
        isPending={isMutating}
      />

      {/* Row 4: Summary */}
      <SummaryCard risk={risk} />

      {/* Shared notes dialog */}
      {showNotesDialog && (
        <ChangeNotesDialog
          title={
            pendingAction === 'scoring'
              ? 'Apply Scoring Changes'
              : pendingAction === 'description'
                ? 'Save Description Changes'
                : pendingAction === 'taxonomy'
                  ? 'Change Taxonomy Category'
                  : 'Confirm State Transition'
          }
          notes={changeNotes}
          onNotesChange={setChangeNotes}
          onConfirm={handleConfirmApply}
          onCancel={handleCancelDialog}
          isPending={isMutating}
        />
      )}
    </div>
  )
}

// --- Change Notes Dialog ---

function ChangeNotesDialog({
  title,
  notes,
  onNotesChange,
  onConfirm,
  onCancel,
  isPending,
}: {
  title: string
  notes: string
  onNotesChange: (v: string) => void
  onConfirm: () => void
  onCancel: () => void
  isPending: boolean
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-base">{title}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="mb-1.5 block text-sm">
              Change notes <span className="text-muted-foreground">(optional)</span>
            </Label>
            <textarea
              className="min-h-[80px] w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              placeholder="Describe the reason for this change..."
              value={notes}
              onChange={(e) => onNotesChange(e.target.value)}
              autoFocus
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={onCancel} disabled={isPending}>
              Cancel
            </Button>
            <Button size="sm" onClick={onConfirm} disabled={isPending}>
              {isPending ? 'Saving...' : 'Apply'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

// --- Scoring Card ---

function ScoringCard({
  scoreType,
  draft,
  likelihood,
  impactTables,
  onLikelihoodChange,
  onImpactChange,
}: {
  scoreType: ScoreType
  draft: ScoringDraft
  likelihood: ActiveLikelihood | null
  impactTables: ImpactTableWithLevels[]
  onLikelihoodChange: (st: ScoreType, levelId: string | null) => void
  onImpactChange: (st: ScoreType, tableId: string, levelId: string | null) => void
}) {
  const config = SCORE_TYPE_CONFIG[scoreType]
  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <div
            className="h-3 w-3 rounded-full"
            style={{ backgroundColor: `hsl(var(${config.colorVar}))` }}
          />
          <CardTitle className="text-sm">{config.title}</CardTitle>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {likelihood && (
          <InlineDropdown
            label="Likelihood"
            currentLevelId={draft.likelihoods[scoreType]}
            levels={likelihood.levels}
            onSelect={(id) => onLikelihoodChange(scoreType, id)}
          />
        )}
        {impactTables.length > 0 && (
          <div className="space-y-2">
            <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Impacts
            </div>
            {impactTables.map((table) => (
              <InlineDropdown
                key={table.id}
                label={table.name}
                currentLevelId={draft.impacts[impactDraftKey(scoreType, table.id)] ?? null}
                levels={table.levels}
                onSelect={(id) => onImpactChange(scoreType, table.id, id)}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

// --- Inline Dropdown ---

function InlineDropdown({
  label,
  currentLevelId,
  levels,
  onSelect,
}: {
  label: string
  currentLevelId: string | null
  levels: { id: string; level_number: number; label: string }[]
  onSelect: (levelId: string | null) => void
}) {
  return (
    <div className="flex items-center gap-3">
      <Label className="w-24 shrink-0 text-xs text-muted-foreground">{label}</Label>
      <select
        className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-ring"
        value={currentLevelId ?? ''}
        onChange={(e) => onSelect(e.target.value || null)}
      >
        <option value="">— Not set —</option>
        {levels.map((l) => (
          <option key={l.id} value={l.id}>
            {l.level_number}. {l.label}
          </option>
        ))}
      </select>
    </div>
  )
}

// --- Description Card ---

function DescriptionCard({
  editing,
  setEditing,
  name,
  setName,
  eventDescription,
  setEventDescription,
  drivers,
  setDrivers,
  impactDescription,
  setImpactDescription,
  isDirty,
  onApply,
  onCancel,
  isPending,
  error,
}: {
  editing: boolean
  setEditing: (v: boolean) => void
  name: string
  setName: (v: string) => void
  eventDescription: string
  setEventDescription: (v: string) => void
  drivers: string
  setDrivers: (v: string) => void
  impactDescription: string
  setImpactDescription: (v: string) => void
  isDirty: boolean
  onApply: () => void
  onCancel: () => void
  isPending: boolean
  error: Error | null
}) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
        <CardTitle className="text-base">Risk Description</CardTitle>
        {!editing ? (
          <Button variant="ghost" size="sm" onClick={() => setEditing(true)}>
            <Pencil className="mr-1 h-3.5 w-3.5" /> Edit
          </Button>
        ) : (
          <div className="flex items-center gap-2">
            {isDirty && <span className="text-xs text-amber-600">Unsaved</span>}
            <Button variant="ghost" size="sm" onClick={onCancel} disabled={isPending}>
              <X className="mr-1 h-3.5 w-3.5" /> Cancel
            </Button>
            <Button size="sm" onClick={onApply} disabled={!isDirty || isPending}>
              <Check className="mr-1 h-3.5 w-3.5" /> Apply Changes
            </Button>
          </div>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {error && (
          <div className="rounded bg-destructive/10 p-2 text-sm text-destructive">
            {error.message || 'Failed to save'}
          </div>
        )}
        <EditableField label="Name" value={name} onChange={setName} editing={editing} />
        <EditableField
          label="Event Description"
          value={eventDescription}
          onChange={setEventDescription}
          editing={editing}
          multiline
        />
        <EditableField
          label="Drivers"
          value={drivers}
          onChange={setDrivers}
          editing={editing}
          multiline
        />
        <EditableField
          label="Impact Description"
          value={impactDescription}
          onChange={setImpactDescription}
          editing={editing}
          multiline
        />
      </CardContent>
    </Card>
  )
}

function EditableField({
  label,
  value,
  onChange,
  editing,
  multiline,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  editing: boolean
  multiline?: boolean
}) {
  return (
    <div>
      <dt className="mb-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </dt>
      {editing ? (
        multiline ? (
          <textarea
            className="min-h-[60px] w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-sm"
            value={value}
            onChange={(e) => onChange(e.target.value)}
          />
        ) : (
          <Input value={value} onChange={(e) => onChange(e.target.value)} />
        )
      ) : (
        <dd className="text-sm">
          {value || <span className="italic text-muted-foreground">Not set</span>}
        </dd>
      )}
    </div>
  )
}

// --- Classification Card with taxonomy selector and lifecycle transitions ---

function ClassificationCard({
  risk,
  taxonomyNodes,
  onTaxonomyChange,
  onTransition,
  isPending,
}: {
  risk: Risk
  taxonomyNodes: TaxonomyNode[]
  onTaxonomyChange: (nodeId: string) => void
  onTransition: (targetState: string) => void
  isPending: boolean
}) {
  const transitions = VALID_TRANSITIONS[risk.lifecycle_state] ?? []
  const [taxonomyDialogOpen, setTaxonomyDialogOpen] = useState(false)

  const handleTaxonomySelect = useCallback(
    (nodeId: string) => {
      setTaxonomyDialogOpen(false)
      onTaxonomyChange(nodeId)
    },
    [onTaxonomyChange],
  )

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Classification & Ownership</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <dt className="mb-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Taxonomy Category
            </dt>
            <dd>
              {taxonomyNodes.length > 0 ? (
                <Button
                  variant="outline"
                  className="w-full justify-between font-normal"
                  size="sm"
                  disabled={isPending}
                  onClick={() => setTaxonomyDialogOpen(true)}
                >
                  <span className="truncate">{risk.taxonomy_node_name}</span>
                  <ChevronDown className="ml-2 h-3.5 w-3.5 shrink-0 opacity-50" />
                </Button>
              ) : (
                <span className="text-sm">{risk.taxonomy_node_name}</span>
              )}
            </dd>
          </div>
          <ReadField label="Owner" value={risk.owner_display_name} />
          <div>
            <dt className="mb-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Lifecycle State
            </dt>
            <dd className="flex items-center gap-3">
              <Badge
                variant="outline"
                className={cn('text-xs', lifecycleStateColors[risk.lifecycle_state])}
              >
                {lifecycleStateLabels[risk.lifecycle_state]}
              </Badge>
              {transitions.map((t) => (
                <Button
                  key={t.target}
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs"
                  disabled={isPending}
                  onClick={() => onTransition(t.target)}
                >
                  <ArrowRight className="mr-1 h-3 w-3" />
                  {t.label}
                </Button>
              ))}
            </dd>
          </div>
          <ReadField label="Created Via" value={risk.created_via} />
        </div>
      </CardContent>

      {taxonomyDialogOpen && (
        <TaxonomyPickerDialog
          nodes={taxonomyNodes}
          currentNodeId={risk.taxonomy_node_id}
          currentNodeName={risk.taxonomy_node_name}
          currentNodePath={risk.taxonomy_node_path}
          onSelect={handleTaxonomySelect}
          onClose={() => setTaxonomyDialogOpen(false)}
        />
      )}
    </Card>
  )
}

// --- Taxonomy Picker Dialog ---

interface TaxTreePickerNode {
  id: string
  name: string
  depth: number
  children: TaxTreePickerNode[]
}

function buildPickerTree(nodes: TaxonomyNode[]): TaxTreePickerNode[] {
  const sorted = [...nodes].sort((a, b) => a.path.localeCompare(b.path))
  const roots: TaxTreePickerNode[] = []
  const stack: TaxTreePickerNode[] = []

  for (const n of sorted) {
    const treeNode: TaxTreePickerNode = { id: n.id, name: n.name, depth: n.depth, children: [] }
    // Pop stack until we find the parent (depth - 1)
    while (stack.length > 0 && stack[stack.length - 1]!.depth >= n.depth) {
      stack.pop()
    }
    if (stack.length > 0) {
      stack[stack.length - 1]!.children.push(treeNode)
    } else {
      roots.push(treeNode)
    }
    stack.push(treeNode)
  }
  return roots
}

/** Collect all ancestor node IDs for a given node ID so we can expand the path to it. */
function findAncestorIds(nodes: TaxonomyNode[], targetId: string): Set<string> {
  const ids = new Set<string>()
  // Walk up from target by matching path prefixes
  const target = nodes.find((n) => n.id === targetId)
  if (!target) return ids
  // For each node whose path is a strict prefix of the target's path, it's an ancestor
  for (const n of nodes) {
    if (n.id !== targetId && target.path.startsWith(n.path) && target.path[n.path.length] === '.') {
      ids.add(n.id)
    }
  }
  return ids
}

/** Find the best matching node in the current taxonomy for a stale risk.
 *  Strategy:
 *  1. Exact path match — the node at the same materialized path position (most reliable)
 *  2. Longest path prefix match — nearest ancestor still exists
 *  3. Exact name match (deepest wins) — fallback when tree was restructured
 *  4. null — no reasonable match found
 */
function findBestMatch(
  nodes: TaxonomyNode[],
  stalePath: string,
  staleName: string,
): string | null {
  // 1. Exact path match — same position in new taxonomy
  const exactPath = nodes.find((n) => n.path === stalePath)
  if (exactPath) return exactPath.id

  // 2. Longest path prefix — walk up from the stale path to find nearest ancestor
  let prefix = stalePath
  while (prefix.length > 0) {
    const lastDot = prefix.lastIndexOf('.')
    if (lastDot < 0) break
    prefix = prefix.substring(0, lastDot)
    const ancestor = nodes.find((n) => n.path === prefix)
    if (ancestor) return ancestor.id
  }

  // 3. Exact name match (deepest wins)
  const lowerName = staleName.toLowerCase()
  const nameMatches = nodes
    .filter((n) => n.name.toLowerCase() === lowerName)
    .sort((a, b) => b.depth - a.depth)
  if (nameMatches.length > 0) return nameMatches[0]!.id

  return null
}

function TaxonomyPickerDialog({
  nodes,
  currentNodeId,
  currentNodeName,
  currentNodePath,
  onSelect,
  onClose,
}: {
  nodes: TaxonomyNode[]
  currentNodeId: string
  currentNodeName: string
  currentNodePath: string
  onSelect: (nodeId: string) => void
  onClose: () => void
}) {
  const tree = useMemo(() => buildPickerTree(nodes), [nodes])

  // Determine which node to highlight and expand to
  const isStale = !nodes.some((n) => n.id === currentNodeId)
  const highlightId = isStale ? findBestMatch(nodes, currentNodePath, currentNodeName) : currentNodeId

  // Build initial expanded set: all ancestors of the highlighted node
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => {
    if (!highlightId) return new Set<string>()
    const ancestors = findAncestorIds(nodes, highlightId)
    // Also expand the highlighted node itself in case it has children
    ancestors.add(highlightId)
    return ancestors
  })

  const toggleExpand = useCallback((id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <Card className="w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Select Taxonomy Category</CardTitle>
          {isStale && (
            <p className="text-xs text-amber-600">
              Current category "{currentNodeName}" is from a previous framework version.
              {highlightId ? ' Nearest match highlighted below.' : ''}
            </p>
          )}
        </CardHeader>
        <CardContent>
          <div className="max-h-80 overflow-y-auto rounded-md border p-1">
            {tree.map((node) => (
              <PickerTreeNode
                key={node.id}
                node={node}
                depth={0}
                expandedIds={expandedIds}
                highlightId={highlightId}
                currentNodeId={currentNodeId}
                onToggle={toggleExpand}
                onSelect={onSelect}
              />
            ))}
          </div>
          <div className="mt-4 flex justify-end">
            <Button variant="outline" size="sm" onClick={onClose}>
              Cancel
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

function PickerTreeNode({
  node,
  depth,
  expandedIds,
  highlightId,
  currentNodeId,
  onToggle,
  onSelect,
}: {
  node: TaxTreePickerNode
  depth: number
  expandedIds: Set<string>
  highlightId: string | null
  currentNodeId: string
  onToggle: (id: string) => void
  onSelect: (id: string) => void
}) {
  const isExpanded = expandedIds.has(node.id)
  const hasChildren = node.children.length > 0
  const isHighlighted = node.id === highlightId
  const isCurrent = node.id === currentNodeId
  const paddingLeft = depth * 20 + 8

  return (
    <div>
      <div
        className={cn(
          'flex cursor-pointer items-center gap-1.5 rounded-sm px-2 py-1.5 text-sm',
          'transition-colors hover:bg-accent/50',
          isHighlighted && 'bg-accent font-medium text-accent-foreground',
          isCurrent && !isHighlighted && 'bg-accent/30',
        )}
        style={{ paddingLeft }}
        onClick={() => onSelect(node.id)}
      >
        <button
          className={cn(
            'flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-sm hover:bg-accent',
            !hasChildren && 'invisible',
          )}
          onClick={(e) => {
            e.stopPropagation()
            onToggle(node.id)
          }}
        >
          {hasChildren &&
            (isExpanded ? (
              <ChevronDown className="h-3.5 w-3.5" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5" />
            ))}
        </button>
        <span className="flex-1 truncate" title={node.name}>
          {node.name}
        </span>
        {isCurrent && (
          <Check className="h-3.5 w-3.5 shrink-0 text-primary" />
        )}
      </div>
      {isExpanded && hasChildren && (
        <div>
          {node.children.map((child) => (
            <PickerTreeNode
              key={child.id}
              node={child}
              depth={depth + 1}
              expandedIds={expandedIds}
              highlightId={highlightId}
              currentNodeId={currentNodeId}
              onToggle={onToggle}
              onSelect={onSelect}
            />
          ))}
        </div>
      )}
    </div>
  )
}

// --- Summary Card ---

function SummaryCard({ risk }: { risk: Risk }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Summary</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-3 gap-4 text-sm">
          <SummaryItem label="Linked Controls" value={risk.linked_control_count} />
          <SummaryItem label="Linked Actions" value={risk.linked_action_count} />
          <SummaryItem label="Child Risks" value={risk.child_risk_count} />
        </div>
      </CardContent>
    </Card>
  )
}

// --- Shared helpers ---

function ReadField({ label, value }: { label: string; value?: string | null }) {
  return (
    <div>
      <dt className="mb-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </dt>
      <dd className="text-sm">
        {value || <span className="italic text-muted-foreground">Not set</span>}
      </dd>
    </div>
  )
}

function SummaryItem({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded bg-muted/30 p-3 text-center">
      <div className="text-2xl font-semibold tabular-nums">{value}</div>
      <div className="mt-1 text-xs text-muted-foreground">{label}</div>
    </div>
  )
}

// --- Grid data builders ---

function buildGridConfig(
  likelihood: ActiveLikelihood | null,
  impactTables: ImpactTableWithLevels[],
): GridConfig | null {
  if (!likelihood || likelihood.levels.length === 0) return null
  const impactTable = impactTables[0]
  if (!impactTable || impactTable.levels.length === 0) return null
  return {
    likelihoodLevels: likelihood.levels.map((l) => ({ value: l.level_number, label: l.label })),
    impactLevels: impactTable.levels.map((l) => ({ value: l.level_number, label: l.label })),
  }
}

function buildAppetiteMap(
  cells: { likelihood_level: number; impact_level: number }[],
): AppetiteMap {
  const map: AppetiteMap = {}
  for (const cell of cells) {
    map[appetiteKey(cell.likelihood_level, cell.impact_level)] = true
  }
  return map
}

function buildJourneyData(
  risk: Risk,
  likelihood: ActiveLikelihood | null,
  _impactTables: ImpactTableWithLevels[],
  assessments: ImpactAssessment[],
): RiskJourneyData | null {
  if (!likelihood) return null
  const resolveLikelihood = (levelId: string | null | undefined): number | null => {
    if (!levelId) return null
    return likelihood.levels.find((l) => l.id === levelId)?.level_number ?? null
  }
  const resolveImpact = (scoreType: ScoreType): number | null => {
    const matching = assessments.filter((a) => a.score_type === scoreType)
    if (matching.length === 0) return null
    return Math.max(...matching.map((a) => a.level_number))
  }
  const pos = (scoreType: ScoreType, likelihoodId: string | null | undefined) => {
    const l = resolveLikelihood(likelihoodId)
    const i = resolveImpact(scoreType)
    return l !== null && i !== null ? { likelihood: l, impact: i } : null
  }
  return {
    id: risk.id,
    name: risk.name,
    inherent: pos('inherent', risk.inherent_likelihood_level_id),
    residual: pos('residual', risk.residual_likelihood_level_id),
    target: pos('target', risk.target_likelihood_level_id),
  }
}

function buildJourneyDataFromDraft(
  risk: Risk,
  draft: ScoringDraft,
  likelihood: ActiveLikelihood | null,
  impactTables: ImpactTableWithLevels[],
): RiskJourneyData | null {
  if (!likelihood) return null
  const resolveLikelihood = (levelId: string | null): number | null => {
    if (!levelId) return null
    return likelihood.levels.find((l) => l.id === levelId)?.level_number ?? null
  }
  const resolveImpact = (scoreType: ScoreType): number | null => {
    const levels: number[] = []
    for (const table of impactTables) {
      const levelId = draft.impacts[impactDraftKey(scoreType, table.id)]
      if (!levelId) continue
      const level = table.levels.find((l) => l.id === levelId)
      if (level) levels.push(level.level_number)
    }
    return levels.length > 0 ? Math.max(...levels) : null
  }
  const pos = (scoreType: ScoreType) => {
    const l = resolveLikelihood(draft.likelihoods[scoreType])
    const i = resolveImpact(scoreType)
    return l !== null && i !== null ? { likelihood: l, impact: i } : null
  }
  return {
    id: risk.id,
    name: risk.name,
    inherent: pos('inherent'),
    residual: pos('residual'),
    target: pos('target'),
  }
}
